/**
 * Companies Admin App
 *
 * Main application component for the companies admin interface.
 * Uses React Query for efficient data fetching and caching.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { fetchConnections } from '@/portal/src/api/integrationCompanies';
import { hasActiveConnectorForImport, sanitizeIntegrationListPayload } from '@/hub-shared/utils/integrationConnections';
import { fetchCompanies, deleteCompany, handleApiError } from './utils/api';
import CompaniesTable from './components/CompaniesTable';
import CompanyForm from './components/CompanyForm';
import { getBootstrapData } from '@tpct/admin-ui';
import type { BreadcrumbItem } from '@tpct/admin-ui';
import BreadcrumbBar from '@tpct/mission-core/components/BreadcrumbBar';
import HubAccessBlocked from '@/hub-shared/components/HubAccessBlocked';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalSearchSyncFn } from '@/portal/src/utils/portalUrlState';
import { parsePortalSearchParams, parsePositiveIntParam } from '@/portal/src/utils/portalUrlState';

type View = 'table' | 'create';

type CompaniesAppProps = {
  bootstrapKey?: string;
  navigateTo?: (path: string) => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const PAGE_SIZE = 10;

const CompaniesApp = ({
  bootstrapKey = 'AccountingHubCompaniesAdmin',
  navigateTo,
  search,
  patchPortalSearch,
}: CompaniesAppProps) => {
  const portalMode = typeof patchPortalSearch === 'function';
  const params = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);

  const bootstrapData = useMemo(() => getBootstrapData(bootstrapKey), [bootstrapKey]);

  const canCreateEnterprises =
    (bootstrapData as { capabilities?: { createEnterprises?: boolean } } | null)?.capabilities
      ?.createEnterprises === true;

  const urlView: View = useMemo(() => {
    if (!portalMode) {
      return 'table';
    }
    if (params.get('view') === 'create' && !canCreateEnterprises) {
      return 'table';
    }
    return params.get('view') === 'create' ? 'create' : 'table';
  }, [canCreateEnterprises, params, portalMode]);

  const urlPage = useMemo(
    () => (portalMode ? parsePositiveIntParam(params.get('page'), 1, { max: 9999 }) : 1),
    [params, portalMode],
  );

  const [standaloneView, setStandaloneView] = useState<View>('table');
  const [standalonePage, setStandalonePage] = useState(1);

  const view = portalMode
    ? urlView
    : !canCreateEnterprises && standaloneView === 'create'
      ? 'table'
      : standaloneView;
  const page = portalMode ? urlPage : standalonePage;

  const queryClient = useQueryClient();

  const connectionsQuery = useQuery({
    queryKey: ['connections-list', bootstrapData?.restUrl, bootstrapKey],
    queryFn: async () => {
      const payload = await fetchConnections({
        baseUrl: bootstrapData!.restUrl,
        nonce: bootstrapData!.nonce,
      });
      return sanitizeIntegrationListPayload(payload);
    },
    enabled: !!bootstrapData?.restUrl && !!bootstrapData?.nonce && view === 'create' && !!navigateTo,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const connectorImportVisible =
    !!navigateTo &&
    (connectionsQuery.isError ||
      (!connectionsQuery.isLoading && hasActiveConnectorForImport(connectionsQuery.data ?? [])));

  const {
    data: companiesData,
    isLoading: companiesLoading,
    error: companiesError,
  } = useQuery({
    queryKey: ['companies', page],
    queryFn: async () => fetchCompanies({ page, per_page: PAGE_SIZE }, bootstrapKey),
    enabled: !!bootstrapData && view === 'table',
  });

  const deleteMutation = useMutation({
    mutationFn: (companyId: number) => deleteCompany(companyId, bootstrapKey),
    onSuccess: () => {
      if (patchPortalSearch) {
        patchPortalSearch({ page: null }, 'replace');
      } else {
        setStandalonePage(1);
      }
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const companies = companiesData?.companies || [];
  const cabinetSelfEntry = companiesData?.cabinet_self_entry ?? null;
  const loading = companiesLoading;
  const error = companiesError ? handleApiError(companiesError) : null;
  const listFetchBlocked = Boolean(error && view === 'table');
  const brandLogoUrl = bootstrapData?.brand?.logoUrl;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  const goTable = useCallback(() => {
    if (patchPortalSearch) {
      patchPortalSearch(
        {
          view: null,
          page: null,
          cabinetImport: null,
          cabinetImportStep: null,
        },
        'push',
      );
    } else {
      setStandaloneView('table');
    }
    queryClient.invalidateQueries({ queryKey: ['companies'] });
  }, [patchPortalSearch, queryClient]);

  const handleCreateCompany = () => {
    if (!canCreateEnterprises) {
      return;
    }
    if (patchPortalSearch) {
      patchPortalSearch({ view: 'create', page: null, cabinetImport: null, cabinetImportStep: null }, 'push');
    } else {
      setStandaloneView('create');
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    try {
      await deleteMutation.mutateAsync(companyId);
      handleRefresh();
    } catch (err) {
      alert(handleApiError(err));
    }
  };

  const handleCancelForm = () => {
    goTable();
  };

  const handleCabinetFileImportComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['companies'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient]);

  const handleFormSave = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    goTable();
  };

  const handlePageChange = (nextPage: number) => {
    if (patchPortalSearch) {
      patchPortalSearch({ page: nextPage <= 1 ? null : String(nextPage) }, 'replace');
    } else {
      setStandalonePage(nextPage);
    }
  };

  useEffect(() => {
    if (!portalMode || !patchPortalSearch) {
      return;
    }
    if (params.get('cabinetImport') === '1' && view !== 'create') {
      patchPortalSearch({ cabinetImport: null, cabinetImportStep: null }, 'replace');
    }
  }, [params, patchPortalSearch, portalMode, view]);

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    if (view === 'table') {
      items.push({ label: __('Mes dossiers') });
    } else if (view === 'create') {
      items.push({
        label: __('Mes dossiers'),
        onClick: goTable,
      });
      items.push({ label: __('Nouvelle entreprise') });
    }

    return items;
  };

  return (
    <>
      <BreadcrumbBar items={getBreadcrumbItems()} />

      {listFetchBlocked && error ? (
        <HubAccessBlocked message={error} onRetry={handleRefresh} logoUrl={brandLogoUrl} />
      ) : view === 'table' ? (
        <>
          <CompaniesTable
            companies={companies}
            cabinetSelfEntry={cabinetSelfEntry}
            loading={loading}
            page={page}
            totalPages={companiesData?.total_pages ?? 1}
            onPageChange={handlePageChange}
            onRefresh={handleRefresh}
            onDelete={handleDeleteCompany}
            navigateTo={navigateTo}
            bootstrapKey={bootstrapKey}
          />
          {canCreateEnterprises ? (
            <button
              type="button"
              className="btn-primary add-to-list-btn"
              onClick={handleCreateCompany}
              style={{ marginTop: '1rem' }}
            >
              {__('Créer une nouvelle entreprise')}
            </button>
          ) : null}
        </>
      ) : (
        <CompanyForm
          company={null}
          onSave={handleFormSave}
          onCancel={handleCancelForm}
          bootstrapKey={bootstrapKey}
          navigateTo={navigateTo}
          connectorImportVisible={connectorImportVisible}
          onCabinetFileImportComplete={handleCabinetFileImportComplete}
          search={portalMode ? search : undefined}
          patchPortalSearch={patchPortalSearch}
        />
      )}
    </>
  );
};

export default CompaniesApp;
